import { useState, type FormEvent } from "react";
import { LazyMarkdownEditor } from "@/book/components/lazy-editor";
import { ThankYou } from "@/book/components/thank-you";
import { useSubmitReview, type Review } from "@/shared/api/book";
import { errorMessage } from "@/shared/api/client";
import { Button } from "@/shared/ui/button";
import { CheckboxRow } from "@/shared/ui/checkbox";
import { ErrorNote } from "@/shared/ui/state";

/** Vélemény a könyvről — a kezdőlapról és a profilból is elérhető (rendszerterv 8.).
 *  A szöveg **Markdownként** megy a szerverre (a közös szerkesztő, lustán betöltve); a
 *  melléklet, az admin és a publikus `/konyv` oldal is Markdownként rendereli.
 *  A `public_ok` jelöléssel a vélemény jóváhagyás után a `/konyv` oldalra is kikerülhet.
 *  `review` megadásával a saját, korábbi véleményt szerkeszti — ilyenkor a módosított
 *  szöveg **újra jóváhagyásra vár**, és addig lekerül a könyv oldaláról. */
export function ReviewForm({ review, onDone }: { review?: Review; onDone: () => void }) {
  const [body, setBody] = useState(review?.body ?? "");
  const [publicOk, setPublicOk] = useState(review?.public_ok ?? false);
  const submit = useSubmitReview();
  const [sent, setSent] = useState(false);
  const editing = review !== undefined;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit.mutate(
      { id: review?.id, body: body.trim(), public_ok: publicOk },
      { onSuccess: () => setSent(true) },
    );
  };

  if (sent) {
    return editing ? (
      <ThankYou title="Köszönjük, elmentettük!" onDone={onDone}>
        A módosított vélemény újra jóváhagyásra vár, és addig nem jelenik meg a könyv oldalán.
      </ThankYou>
    ) : (
      <ThankYou title="Köszönjük a véleményed!" onDone={onDone}>
        Jóváhagyás után megjelenik a véleményed. Ha engedted, a szerzők a könyv nyilvános
        oldalára is kitehetik.
      </ThankYou>
    );
  }
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <LazyMarkdownEditor
        label="A véleményed"
        value={body}
        onChange={setBody}
        placeholder="Mit adott a könyv, mi maradt meg belőle?"
        rows={6}
      />
      <CheckboxRow id="rev-public" checked={publicOk} onCheckedChange={setPublicOk}>
        Nyilvánosan is megjelenhet
      </CheckboxRow>
      <p className="text-[12px] leading-[1.45] text-muted">
        Ha később törlöd a fiókodat, a vélemény a keresztneveddel megmarad.
      </p>
      {editing && (
        <p className="text-[12px] leading-[1.45] text-muted">
          A módosított vélemény újra jóváhagyásra vár, és addig nem jelenik meg a könyv oldalán.
        </p>
      )}
      {submit.isError && <ErrorNote>{errorMessage(submit.error)}</ErrorNote>}
      <Button type="submit" block size="lg" disabled={submit.isPending || body.trim().length < 10}>
        {submit.isPending ? "Küldés…" : editing ? "Mentem a módosítást" : "Beküldöm"}
      </Button>
    </form>
  );
}
